import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Divider,
  InputLabel,
  FormControl,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Alert,
  Typography,
  TextField,
} from '@mui/material';
import { Save, Cancel, PersonAdd } from '@mui/icons-material';

import PermissionsSection from '@/components/PermissionsSection/PermissionsSection';
import OrganizationsDropdown from '@/components/OrganizationsDropdown/OrganizationsDropdown';
import RolesDropdown from '@/components/RolesDropdown/RolesDropdown';
import FacilityPermissionsDropdown from '@/components/FacilityPermissionsDropdown/FacilityPermissionsDropdown';
import CpdActionButton from '@/components/ui/CpdActionButton';
import MirrorPermissionsDialog from './MirrorPermissionsDialog';

import { supportedLanguages } from '@/translations/config';
import FacilityAccessComponent from './FacilityAccessComponent';
import { FACILITY_USER_ACCESS_MODE } from '@/constants/facilityUserAccessModes';
import { useUserFormDialog } from './useUserFormDialog';

const UserFormDialog = ({ open, onClose, onSave, loading = false }) => {
  const { t } = useTranslation();
  const {
    formData,
    formErrors,
    selectedFacilities,
    selectedOrganization,
    facilityPermissions,
    organizationPermissions,
    applyToAllFacilities,
    selectedFacilityForConfig,
    permissionsType,
    selectedRoleId,
    accessMode,
    selectedFacilityGroupIds,
    roleBasedFacilitiesAccess,
    mirrorDialogOpen,
    mirrorPermissionType,
    applyMirroredPermissions,
    isFormValid,
    getConfiguredFacilitiesCount,
    handleAccessModeChange,
    handleApplyToAllFacilitiesToggleChange,
    handleApplyTemplate,
    handleClose,
    handleEmailChange,
    handleFacilityPermissionChange,
    handleLanguagePreferenceChange,
    handleMfaTypeChange,
    handleOpenFacilityMirrorDialog,
    handleOpenOrganizationMirrorDialog,
    handleOrganizationPermissionChange,
    handleOrganizationSelectChange,
    handlePermissionsTypeToggleChange,
    handleRoleChange,
    handleSave,
    handleUserNameChange,
    closeMirrorDialog,
    setRoleBasedFacilitiesAccess,
    setSelectedFacilities,
    setSelectedFacilityForConfig,
    setSelectedFacilityGroupIds,
  } = useUserFormDialog({ open, onClose, onSave });

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth='md'
      fullWidth
      PaperProps={{
        sx: { minHeight: '70vh', maxHeight: '90vh' },
      }}>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonAdd color='primary' />
          Create New User
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{ overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ pt: 2, overflow: 'auto', flex: 1 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant='h6' gutterBottom>
              Basic Information
            </Typography>

            <Box
              sx={{
                display: 'grid',
                gap: 2,
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              }}>
              <TextField
                type='email'
                label={t('facilityUserManagement.modal.emailUsernameLabel')}
                value={formData.email}
                onChange={handleEmailChange}
                required
                size='small'
                fullWidth
                error={!!formErrors.email}
                helperText={formErrors.email}
                autoComplete='off'
              />

              <TextField
                type='text'
                label={t('facilityUserManagement.modal.nameLabel')}
                value={formData.userName}
                onChange={handleUserNameChange}
                required
                size='small'
                fullWidth
                error={!!formErrors.userName}
                helperText={formErrors.userName}
                autoComplete='off'
              />

              <OrganizationsDropdown
                value={selectedOrganization}
                onChange={handleOrganizationSelectChange}
                label='Organization'
                size='small'
                fullWidth
                required
              />

              <FormControl variant='outlined' size='small' fullWidth>
                <InputLabel id='language-preference'>
                  {t('facilityUserManagement.modal.languagePreferenceLabel')}
                </InputLabel>
                <Select
                  labelId='language-preference'
                  value={formData.languagePreference}
                  onChange={handleLanguagePreferenceChange}
                  label={t(
                    'facilityUserManagement.modal.languagePreferenceLabel',
                  )}>
                  {supportedLanguages.map((lang) => (
                    <MenuItem key={lang.value} value={lang.value}>
                      {lang.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl variant='outlined' size='small' fullWidth>
                <InputLabel id='mfa-type'>MFA Type</InputLabel>
                <Select
                  labelId='mfa-type'
                  value={formData.mfa_type}
                  onChange={handleMfaTypeChange}
                  label='MFA Type'>
                  <MenuItem value={'NONE'}>None</MenuItem>
                  <MenuItem value={'EMAIL_OTP'}>Email OTP</MenuItem>
                  <MenuItem value={'SOFTWARE_TOKEN_MFA'}>Software MFA</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {formErrors.facilities && (
              <Box sx={{ color: 'error.main', mt: 1, fontSize: '0.75rem' }}>
                {formErrors.facilities}
              </Box>
            )}
            {formErrors.role && (
              <Box sx={{ color: 'error.main', mt: 1, fontSize: '0.75rem' }}>
                {formErrors.role}
              </Box>
            )}
          </Box>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ mb: 3 }}>
            <Typography variant='h6' gutterBottom>
              Permissions Assignment
            </Typography>
            <FormControlLabel
              sx={{ ml: 0.5 }}
              control={
                <Switch
                  checked={permissionsType === 'role'}
                  onChange={handlePermissionsTypeToggleChange}
                  color='primary'
                  disabled={
                    accessMode === FACILITY_USER_ACCESS_MODE.FACILITY_GROUPS &&
                    permissionsType === 'role'
                  }
                />
              }
              label='Use Role-based Permissions'
            />

            {permissionsType === 'role' && (
              <Box sx={{ mb: 3, mt: 2 }}>
                {!selectedOrganization && (
                  <Alert severity='warning' sx={{ mb: 2 }}>
                    Please select an organization in the Basic Information
                    section first to see available roles.
                  </Alert>
                )}
                <RolesDropdown
                  value={selectedRoleId || ''}
                  onRoleChange={handleRoleChange}
                  label='Select Role'
                  fullWidth
                  size='small'
                  showDescription={true}
                  organizationId={selectedOrganization}
                  setRoleBasedFacilitiesAccess={setRoleBasedFacilitiesAccess}
                />
                {selectedRoleId && (
                  <Alert severity='info' sx={{ mt: 2 }}>
                    This user will inherit all permissions from the selected
                    role.
                  </Alert>
                )}
              </Box>
            )}
          </Box>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ mb: 3 }}>
            <Typography variant='h6' gutterBottom>
              Facility Assignment
            </Typography>

            <FacilityAccessComponent
              accessMode={accessMode}
              onAccessModeChange={handleAccessModeChange}
              permissionsType={permissionsType}
              roleBasedFacilitiesAccess={roleBasedFacilitiesAccess}
              selectedFacilities={selectedFacilities}
              onSelectedFacilitiesChange={setSelectedFacilities}
              selectedFacilityGroupIds={selectedFacilityGroupIds}
              onSelectedFacilityGroupIdsChange={setSelectedFacilityGroupIds}
              selectedOrganization={selectedOrganization}
              errors={formErrors}
            />
          </Box>

          {permissionsType === 'permission' && selectedFacilities.length > 0 && (
            <>
              {selectedOrganization ? (
                <>
                  <Typography variant='h6' gutterBottom>
                    Organization Permissions
                  </Typography>
                  <Alert severity='info' sx={{ mb: 3 }}>
                    <Typography variant='body2'>
                      Permissions for the selected organization will be
                      configured below.
                    </Typography>
                  </Alert>
                  <PermissionsSection
                    permissions={organizationPermissions}
                    onPermissionChange={handleOrganizationPermissionChange}
                    onApplyTemplate={handleApplyTemplate}
                    permissionLevel='organization'
                    onMirrorClick={handleOpenOrganizationMirrorDialog}
                  />
                </>
              ) : (
                <>
                  <Typography variant='h6' gutterBottom>
                    Organization Permissions
                  </Typography>
                  <Alert severity='warning' sx={{ mb: 3 }}>
                    <Typography variant='body2'>
                      No organization selected. Please select an organization in
                      the Basic Information section to configure permissions.
                    </Typography>
                  </Alert>
                </>
              )}

              <Divider sx={{ my: 3 }} />

              <Box sx={{ mb: 3 }}>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <Typography variant='h6'>Facility Permissions</Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={applyToAllFacilities}
                        onChange={handleApplyToAllFacilitiesToggleChange}
                        color='primary'
                      />
                    }
                    label={`Apply to all ${selectedFacilities.length} selected facilities`}
                  />
                </Box>

                {applyToAllFacilities ? (
                  <Alert severity='info' sx={{ mb: 2 }}>
                    These permissions will be applied to all{' '}
                    {selectedFacilities.length} selected facilities.
                  </Alert>
                ) : (
                  <>
                    <Box sx={{ mb: 2 }}>
                      <FacilityPermissionsDropdown
                        selectedFacilities={selectedFacilities}
                        permissions={facilityPermissions}
                        selectedFacility={selectedFacilityForConfig}
                        onFacilityChange={setSelectedFacilityForConfig}
                        organizationId={selectedOrganization}
                      />

                      <Typography variant='body2' color='text.secondary'>
                        {getConfiguredFacilitiesCount()}/
                        {selectedFacilities.length} facilities configured
                      </Typography>
                    </Box>
                  </>
                )}

                <PermissionsSection
                  permissions={
                    applyToAllFacilities
                      ? facilityPermissions
                      : facilityPermissions[selectedFacilityForConfig] || {}
                  }
                  onPermissionChange={handleFacilityPermissionChange}
                  onApplyTemplate={handleApplyTemplate}
                  permissionLevel='facility'
                  onMirrorClick={handleOpenFacilityMirrorDialog}
                />
              </Box>
            </>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 3 }}>
        <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
          <Box sx={{ flex: 1 }} />

          <Button onClick={handleClose} startIcon={<Cancel />}>
            Cancel
          </Button>

          <CpdActionButton
            onClick={handleSave}
            loading={loading}
            disabled={!isFormValid}
            startIcon={<Save sx={{ color: 'white' }} />}>
            {loading ? 'Creating...' : 'Create User'}
          </CpdActionButton>
        </Box>
      </DialogActions>

      <MirrorPermissionsDialog
        open={mirrorDialogOpen}
        onClose={closeMirrorDialog}
        targetUser={{ name: formData.userName, email: formData.email }}
        permissionType={mirrorPermissionType}
        organizationId={selectedOrganization}
        onApplyToForm={applyMirroredPermissions}
      />
    </Dialog>
  );
};

export default UserFormDialog;
